import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useToast from "../../hooks/useToast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComments,
  faUserPlus,
  faArchive,
  faInfoCircle,
  faTrophy,
  faUsers,
  faCrown,
  faEnvelope,
  faCalendarPlus,
  faRedo,
  faBolt,
} from "@fortawesome/free-solid-svg-icons";

import { useLeagueDetails } from "../../hooks/league/useLeagueDetails";
import { useFetchUser } from "../../hooks/user/useFetchUser";
import { useInviteMember } from "../../hooks/league/useInviteMember";
import { useResendInvite } from "../../hooks/league/useResendInvite";
import { useRemoveMemberFromLeague } from "../../hooks/league/useRemoveMemberFromLeague";
import { useUpdateLeagueChatUrl } from "../../hooks/league/useUpdateLeagueChatUrl";
import { useUpdateLeagueStatus } from "../../hooks/league/useUpdateLeagueStatus";
import { useAddLeagueEvents } from "../../hooks/league/useAddLeagueEvents";
import { useFetchScheduledEvents } from "../../hooks/events/useFetchScheduledEvents";
import { useAutoSelectLineup } from "../../hooks/lineup/useAutoSelectLineup";

import Loading from "../../components/common/Loading";
import MemberInviteModal from "../../components/league/MemberInviteModal";
import AddEventsModal from "../../components/league/AddEventsModal";
import MemberDetail from "../../components/league/MemberDetail";
import SetLeagueChatModal from "../../components/league/SetLeagueChatModal";
import LeagueStandings from "../../components/league/LeagueStandings";
import PageHeader from "../../components/common/PageHeader";

const LeagueDetails = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { leagueId: leagueIdFromUrl } = useParams();
  const leagueIdFromRedux = useSelector((state) => state.league.leagueId);

  // Prefer URL param, fallback to Redux for backward compatibility
  const leagueId = leagueIdFromUrl || leagueIdFromRedux;

  const inviteMemberMutation = useInviteMember();
  const resendInviteMutation = useResendInvite();
  const removeMemberMutation = useRemoveMemberFromLeague();
  const addLeagueEventsMutation = useAddLeagueEvents();

  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showAddEventsModal, setShowAddEventsModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState({ id: null, name: "" });
  const [showChatModal, setShowChatModal] = useState(false);
  const [showAutoGenModal, setShowAutoGenModal] = useState(false);
  const [autoGenEventId, setAutoGenEventId] = useState("");
  const [resendCooldowns, setResendCooldowns] = useState({});

  const { data: league, isLoading, error } = useLeagueDetails(leagueId);
  const {
    mutate: updateLeagueChatUrl,
    isLoading: isUpdateLeagueChatLoading,
    error: isUpdateLeagueChatError,
  } = useUpdateLeagueChatUrl();

  const {
    mutate: updateLeagueStatus,
    isLoading: isUpdateLeagueStatusLoading,
    error: isUpdateLeagueStatusError,
  } = useUpdateLeagueStatus();

  const { mutate: autoSelectLineup, isLoading: isAutoGenLoading } =
    useAutoSelectLineup();

  const {
    data: dbUser,
    isLoading: isUserLoading,
    error: userError,
  } = useFetchUser();

  const { data: scheduledEvents } = useFetchScheduledEvents();

  // Redirect if no leagueId in URL (after all hooks)
  useEffect(() => {
    if (!leagueId) {
      navigate("/leagues");
    }
  }, [leagueId, navigate]);

  const handleSetChatUrl = async (url) => {
    updateLeagueChatUrl({ leagueId: leagueId, leagueChatUrl: url });
  };

  const handleInvite = async (userEmail) => {
    try {
      await inviteMemberMutation.mutateAsync({ leagueId, userEmail });
      toast.success("User invited successfully");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Could not invite the user at this time.";
      toast.error(message);
    }
  };

  const handleResendInvite = async (email) => {
    try {
      await resendInviteMutation.mutateAsync({ leagueId, userEmail: email });
      toast.success("Invitation resent successfully.");
      // Start 60-second cooldown for this email
      setResendCooldowns((prev) => ({ ...prev, [email]: true }));
      setTimeout(() => {
        setResendCooldowns((prev) => ({ ...prev, [email]: false }));
      }, 60000);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Could not resend the invitation at this time.";
      toast.error(message);
    }
  };

  const handleAddEvents = async (eventIds) => {
    try {
      await addLeagueEventsMutation.mutateAsync({ leagueId, eventIds });
      toast.success(
        `${eventIds.length} event${eventIds.length > 1 ? "s" : ""} added to the league`,
      );
    } catch (error) {
      const message =
        error?.response?.data?.message || "Could not add events at this time.";
      toast.error(message);
    }
  };

  const handleRemoveClick = (member) => {
    setShowConfirmModal(true);
    setMemberToRemove(member);
  };

  const handleRemoveConfirm = async () => {
    if (memberToRemove) {
      removeMemberMutation.mutate(
        { leagueId, userId: memberToRemove.id },
        {
          onSuccess: () => {
            toast.success("Member successfully removed");
            setShowConfirmModal(false);
            setMemberToRemove(null); // Reset the state
          },
          onError: (error) => {
            toast.error(
              `Error removing member: ${
                error.response?.data?.message || "Please try again later."
              }`,
            );
          },
        },
      );
    }
  };

  const handleOpenLeagueChat = () => {
    if (league.leagueChatUrl) {
      window.open(league.leagueChatUrl, "_blank", "noopener,noreferrer");
    } else {
      toast.info("League chat URL is not set.");
    }
  };

  const handleArchiveConfirm = async () => {
    try {
      updateLeagueStatus({ leagueId, status: "archived" });
      toast.success("League has been archived.");
      setShowArchiveModal(false); // Close the modal
    } catch (error) {
      toast.error("Error archiving the league. Please try again later.");
    }
  };

  if (
    isLoading ||
    isUserLoading ||
    isUpdateLeagueChatLoading ||
    isUpdateLeagueStatusLoading
  )
    return <Loading />;
  if (error) return <div>Error fetching league details: {error.message}</div>;
  if (userError)
    return <div>Error fetching user details: {userError.message}</div>;
  if (isUpdateLeagueChatError) return <div>Error updating the league chat</div>;
  if (isUpdateLeagueStatusError)
    return <div>Error updating the league status</div>;

  const isCreator = league.creatorId === dbUser?._id;

  // This league's events (resolved from the scheduled-events list).
  const leagueEventIdSet = new Set((league.eventIds || []).map(String));
  const leagueEvents = (scheduledEvents || []).filter((e) =>
    leagueEventIdSet.has(String(e.EventId)),
  );

  const handleAutoGenerate = () => {
    const evt = leagueEvents.find(
      (e) => String(e.EventId) === String(autoGenEventId),
    );
    if (!evt) {
      toast.error("Please select an event.");
      return;
    }
    autoSelectLineup(
      { leagueId, eventId: String(evt.EventId), eventName: evt.Name },
      {
        onSuccess: () => {
          toast.success(`Auto-generated missing lineups for ${evt.Name}.`);
          setShowAutoGenModal(false);
          setAutoGenEventId("");
        },
        onError: (error) =>
          toast.error(
            error.response?.data?.message ||
              "Failed to auto-generate lineups.",
          ),
      },
    );
  };

  const openSeats =
    league.numPlayers - league.members.length - (league.invites?.length || 0);
  // const isUserMember = league.members.includes(dbUser?._id);

  // Determine if the league's first event has started
  const hasLeagueStarted = (() => {
    if (league.eventIds?.length > 0 && scheduledEvents?.length > 0) {
      const leagueEventIds = new Set(league.eventIds.map(String));
      const leagueEvents = scheduledEvents.filter((event) =>
        leagueEventIds.has(String(event.EventId)),
      );
      if (leagueEvents.length > 0) {
        const firstEventDate = leagueEvents.reduce((earliest, event) => {
          const eventDate = new Date(event.DateTime);
          return eventDate < earliest ? eventDate : earliest;
        }, new Date(leagueEvents[0].DateTime));
        return new Date() >= firstEventDate;
      }
    }
    // Fallback: if no event data available, use startDate
    return new Date() >= new Date(league.startDate);
  })();
  const formattedStatus =
    league.status.charAt(0).toUpperCase() + league.status.slice(1);
  // const statusColor =
  //   league.status === "active"
  //     ? "bg-green-500"
  //     : league.status === "inactive"
  //     ? "bg-red-500"
  //     : "bg-medium-grey";

  // Format expiration date
  const formatExpirationDate = (dateString) => {
    const expiryDate = new Date(dateString);
    const now = new Date();

    // Calculate days remaining
    const diffTime = expiryDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return "Expired";
    } else if (diffDays === 1) {
      return "Expires tomorrow";
    } else {
      return `Expires in ${diffDays} days`;
    }
  };

  // Combine active members and invited members for display.
  // memberDetails (from the API) carries each member's user data so we don't
  // fetch them one by one.
  const memberUserById = new Map(
    (league.memberDetails || []).map((m) => [String(m._id), m]),
  );
  const allMembers = [
    ...(league.members || []).map((memberId) => ({
      id: memberId,
      user: memberUserById.get(String(memberId)) || null,
      status: "active",
      isCreator: memberId === league.creatorId,
    })),
    ...(league.invites || []).map((invite) => ({
      id: invite._id,
      email: invite.email,
      username: invite.username || null,
      status: "invited",
      expiresAt: invite.expiresAt,
    })),
  ];

  // const getStatusBadge = () => (
  //   <span
  //     className={`px-3 py-1 rounded-full text-white font-semibold text-sm ${statusColor}`}
  //   >
  //     {formattedStatus}
  //   </span>
  // );

  return (
    <div className="space-y-6">
      <PageHeader
        title={league.leagueName}
        subtitle={`Fantasy MMA League • ${formattedStatus}`}
        icon={faTrophy}
        backgroundIcon={faUsers}
      />

      {/* League Info Card */}
      <div className="bg-white rounded-xl border border-light-grey overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-deep-red rounded-lg flex items-center justify-center">
                <FontAwesomeIcon icon={faInfoCircle} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-dark-grey">
                  League Info
                </h2>
                <p className="text-sm text-medium-grey">
                  Current status and details
                </p>
              </div>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-white font-semibold text-sm ${
                league.status === "active"
                  ? "bg-deep-red"
                  : league.status === "inactive"
                    ? "bg-medium-grey"
                    : "bg-light-grey"
              }`}
            >
              {formattedStatus}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-light-grey/20 rounded-lg">
              <div className="text-2xl font-bold text-dark-grey">
                {league.members.length}
              </div>
              <div className="text-sm text-medium-grey">Active Members</div>
            </div>
            <div className="text-center p-4 bg-light-grey/20 rounded-lg">
              <div className="text-2xl font-bold text-dark-grey">
                {openSeats}
              </div>
              <div className="text-sm text-medium-grey">Open Seats</div>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-medium-grey">First Event</span>
              <span className="font-medium text-dark-grey">
                {new Date(league.startDate).toLocaleDateString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-medium-grey">Last Event</span>
              <span className="font-medium text-dark-grey">
                {new Date(league.endDate).toLocaleDateString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-medium-grey">League Chat</span>
              {league.leagueChatUrl ? (
                <button
                  onClick={handleOpenLeagueChat}
                  className="text-deep-red font-medium hover:text-bright-red transition-colors"
                >
                  Open Chat
                </button>
              ) : (
                <span className="text-medium-grey">Not set</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Commissioner Actions - Mobile Optimized */}
      {isCreator && (
        <div className="bg-white rounded-xl border border-light-grey p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-gold rounded-lg flex items-center justify-center">
              <FontAwesomeIcon icon={faCrown} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-dark-grey">Commissioner</h2>
              <p className="text-sm text-medium-grey">Manage your league</p>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => setShowModal(true)}
              disabled={hasLeagueStarted}
              className={`w-full flex items-center justify-center space-x-3 p-4 text-white rounded-xl font-semibold transition-colors ${
                hasLeagueStarted
                  ? "bg-medium-grey cursor-not-allowed"
                  : "bg-deep-red hover:bg-bright-red"
              }`}
              title={
                hasLeagueStarted
                  ? "Cannot invite members after the league has started"
                  : ""
              }
            >
              <FontAwesomeIcon icon={faUserPlus} />
              <span>Invite Members</span>
            </button>
            {hasLeagueStarted && (
              <p className="text-xs text-medium-grey text-center">
                Invitations are locked after the first event starts
              </p>
            )}

            <button
              onClick={() => setShowChatModal(true)}
              className="w-full flex items-center justify-center space-x-3 p-4 bg-white border-2 border-light-grey text-dark-grey rounded-xl font-semibold hover:border-medium-grey transition-colors"
            >
              <FontAwesomeIcon icon={faComments} />
              <span>
                {league.leagueChatUrl ? "Edit Chat URL" : "Set Chat URL"}
              </span>
            </button>

            <button
              onClick={() => setShowAddEventsModal(true)}
              className="w-full flex items-center justify-center space-x-3 p-4 bg-white border-2 border-light-grey text-dark-grey rounded-xl font-semibold hover:border-medium-grey transition-colors"
            >
              <FontAwesomeIcon icon={faCalendarPlus} />
              <span>Add Events</span>
            </button>

            <button
              onClick={() => setShowAutoGenModal(true)}
              className="w-full flex items-center justify-center space-x-3 p-4 bg-white border-2 border-light-grey text-dark-grey rounded-xl font-semibold hover:border-medium-grey transition-colors"
            >
              <FontAwesomeIcon icon={faBolt} />
              <span>Auto-Generate Lineups</span>
            </button>

            <button
              onClick={() => setShowArchiveModal(true)}
              className="w-full flex items-center justify-center space-x-3 p-4 bg-white border-2 border-medium-grey text-medium-grey rounded-xl font-semibold hover:border-dark-grey hover:text-dark-grey transition-colors"
            >
              <FontAwesomeIcon icon={faArchive} />
              <span>Archive League</span>
            </button>
          </div>
        </div>
      )}

      {/* League Standings */}
      <div className="bg-white rounded-xl border border-light-grey overflow-hidden">
        <div className="p-6 border-b border-light-grey">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gold rounded-lg flex items-center justify-center">
              <FontAwesomeIcon icon={faTrophy} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-dark-grey">Standings</h2>
              <p className="text-sm text-medium-grey">
                Current league rankings
              </p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <LeagueStandings leagueId={leagueId} />
        </div>
      </div>

      {/* League Members */}
      <div className="bg-white rounded-xl border border-light-grey overflow-hidden">
        <div className="p-6 border-b border-light-grey">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-deep-red rounded-lg flex items-center justify-center">
                <FontAwesomeIcon icon={faUsers} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-dark-grey">Members</h2>
                <p className="text-sm text-medium-grey">
                  {league.members.length} active • {league.invites?.length || 0}{" "}
                  invited • {openSeats} open
                </p>
              </div>
            </div>
            {isCreator && openSeats > 0 && !hasLeagueStarted && (
              <button
                onClick={() => setShowModal(true)}
                className="p-3 bg-deep-red text-white rounded-lg hover:bg-bright-red transition-colors"
              >
                <FontAwesomeIcon icon={faUserPlus} />
              </button>
            )}
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {/* Active Members */}
            {allMembers
              .filter((member) => member.status === "active")
              .map((member) => (
                <div
                  key={`member-${member.id}`}
                  className={`p-4 rounded-xl border transition-colors ${
                    member.isCreator
                      ? "border-gold/30 bg-gold/5"
                      : "border-light-grey bg-white hover:bg-light-grey/20"
                  }`}
                >
                  {member.isCreator && (
                    <div className="flex items-center space-x-2 mb-3">
                      <FontAwesomeIcon
                        icon={faCrown}
                        className="text-gold text-sm"
                      />
                      <span className="text-sm font-semibold text-gold">
                        Commissioner
                      </span>
                    </div>
                  )}
                  <MemberDetail
                    memberId={member.id}
                    user={member.user}
                    onRemove={(memberId, memberName) =>
                      handleRemoveClick({ id: memberId, name: memberName })
                    }
                    canRemove={isCreator && !member.isCreator}
                    isCreator={member.isCreator}
                    isCommissioner={member.isCreator}
                  />
                </div>
              ))}

            {/* Invited Members */}
            {allMembers
              .filter((member) => member.status === "invited")
              .map((member) => (
                <div
                  key={`invited-${member.id}`}
                  className="p-4 rounded-xl border border-gold/30 bg-gold/5"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <FontAwesomeIcon
                        icon={faEnvelope}
                        className="text-gold text-sm"
                      />
                      <span className="text-sm font-semibold text-gold">
                        Pending Invite
                      </span>
                    </div>
                    <span className="text-xs text-medium-grey">
                      {formatExpirationDate(member.expiresAt)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-9 h-9 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon
                        icon={faUserPlus}
                        className="text-gold text-sm"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      {member.username && (
                        <div className="font-semibold text-dark-grey text-sm truncate">
                          {member.username}
                        </div>
                      )}
                      <div
                        className={`text-medium-grey truncate ${member.username ? "text-xs" : "text-sm font-medium text-dark-grey"}`}
                      >
                        {member.email}
                      </div>
                    </div>
                  </div>
                  {isCreator && !hasLeagueStarted && (
                    <button
                      onClick={() => handleResendInvite(member.email)}
                      disabled={
                        resendCooldowns[member.email] ||
                        resendInviteMutation.isLoading
                      }
                      className={`mt-3 w-full flex items-center justify-center space-x-2 py-2 rounded-lg text-sm font-semibold transition-all ${
                        resendCooldowns[member.email] ||
                        resendInviteMutation.isLoading
                          ? "bg-medium-grey/10 text-medium-grey cursor-not-allowed"
                          : "bg-gold/10 text-gold hover:bg-gold/20 active:scale-[0.98]"
                      }`}
                      title={
                        resendCooldowns[member.email]
                          ? "Please wait before resending"
                          : "Resend invitation email"
                      }
                    >
                      <FontAwesomeIcon
                        icon={faRedo}
                        className={`text-xs ${resendInviteMutation.isLoading ? "animate-spin" : ""}`}
                      />
                      <span>
                        {resendInviteMutation.isLoading
                          ? "Sending..."
                          : resendCooldowns[member.email]
                            ? "Sent ✓"
                            : "Resend Invite"}
                      </span>
                    </button>
                  )}
                </div>
              ))}

            {/* Open Seats */}
            {openSeats > 0 && (
              <div className="p-4 rounded-xl border-2 border-dashed border-light-grey bg-light-grey/10 text-center">
                <FontAwesomeIcon
                  icon={faUserPlus}
                  className="text-medium-grey text-2xl mb-2"
                />
                <p className="text-medium-grey font-medium">
                  {openSeats} open seat{openSeats > 1 ? "s" : ""} remaining
                </p>
                {isCreator && !hasLeagueStarted && (
                  <button
                    onClick={() => setShowModal(true)}
                    className="mt-3 px-4 py-2 bg-deep-red text-white rounded-lg font-semibold hover:bg-bright-red transition-colors"
                  >
                    Invite Members
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Archive Confirmation Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-dark-grey/75 flex justify-center items-center px-4 py-6 z-50">
          <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm w-full">
            <h3 className="text-xl font-bold text-dark-grey mb-4">
              Confirm Archive
            </h3>
            <p className="text-medium-grey mb-6">
              Are you sure you want to archive this league? This action cannot
              be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowArchiveModal(false)}
                className="flex-1 px-4 py-3 border-2 border-light-grey text-dark-grey rounded-xl font-semibold hover:border-medium-grey transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleArchiveConfirm}
                className="flex-1 px-4 py-3 bg-deep-red text-white rounded-xl font-semibold hover:bg-bright-red transition-colors"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {showAutoGenModal && (
        <div className="fixed inset-0 bg-dark-grey/75 flex justify-center items-center px-4 py-6 z-50">
          <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm w-full">
            <h3 className="text-lg font-bold text-dark-grey mb-2">
              Auto-Generate Lineups
            </h3>
            <p className="text-sm text-medium-grey mb-4">
              Create default lineups for members who haven&apos;t submitted for
              the selected event. The global league is never affected.
            </p>
            {leagueEvents.length === 0 ? (
              <p className="text-sm text-medium-grey mb-4">
                No events are associated with this league yet.
              </p>
            ) : (
              <select
                value={autoGenEventId}
                onChange={(e) => setAutoGenEventId(e.target.value)}
                className="w-full border-2 border-light-grey rounded-xl p-3 mb-4"
              >
                <option value="">Select an event…</option>
                {leagueEvents.map((e) => (
                  <option key={e.EventId} value={e.EventId}>
                    {e.Name}
                  </option>
                ))}
              </select>
            )}
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowAutoGenModal(false);
                  setAutoGenEventId("");
                }}
                className="flex-1 px-4 py-3 border-2 border-light-grey text-dark-grey rounded-xl font-semibold hover:border-medium-grey transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAutoGenerate}
                disabled={!autoGenEventId || isAutoGenLoading}
                className="flex-1 px-4 py-3 bg-deep-red text-white rounded-xl font-semibold hover:bg-bright-red transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isAutoGenLoading ? "Generating…" : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}

      <MemberInviteModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onInvite={handleInvite}
      />

      <AddEventsModal
        isOpen={showAddEventsModal}
        onClose={() => setShowAddEventsModal(false)}
        onAdd={handleAddEvents}
        existingEventIds={league?.eventIds || []}
      />

      <SetLeagueChatModal
        isOpen={showChatModal}
        onClose={() => setShowChatModal(false)}
        onSetChatUrl={handleSetChatUrl}
        initialUrl={league.leagueChatUrl || ""}
      />

      {showConfirmModal && (
        <div className="fixed inset-0 bg-dark-grey/75 flex justify-center items-center px-4 py-6 z-50">
          <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm w-full">
            <h3 className="text-xl font-bold text-dark-grey mb-4">
              Remove Member
            </h3>
            <p className="text-medium-grey mb-6">
              Are you sure you want to remove{" "}
              <span className="font-bold">{memberToRemove.name}</span> from the
              league?
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-3 border-2 border-light-grey text-dark-grey rounded-xl font-semibold hover:border-medium-grey transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveConfirm}
                className="flex-1 px-4 py-3 bg-deep-red text-white rounded-xl font-semibold hover:bg-bright-red transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeagueDetails;
